import { Inject, Injectable } from '@angular/core';
import { Image, ImageDisk } from '@frontend/core';

/**
 * The minimum an image needs to be resolvable to a delivery URL.
 * Accepts a full {@link Image} or a bare `(disk, path)` pair — the latter is how
 * user avatars arrive (denormalised `avatar_disk` / `avatar_path`).
 */
export interface ImageUrlSource {
  disk: ImageDisk | string | null | undefined;
  path: string | null | undefined;
}

/**
 * Builds delivery URLs from the disk-agnostic `(disk, path)` pair the backend now
 * emits. Cloudinary is one provider among several: only `cloudinary` paths accept a
 * transformation string; `wordpress` / `external` paths are full URLs used verbatim;
 * `local` paths are served from the app's public storage (`{appUrl}/storage/{path}`).
 *
 * This is the single source of truth for image URL construction — route all image
 * rendering through it (directly or via the `imageUrl` pipe).
 */
@Injectable({
  providedIn: 'root',
})
export class ImageUrlService {
  constructor(
    @Inject('imageHostIdentifier') private imageHostIdentifier: string,
    @Inject('appUrl') private appUrl: string
  ) {}

  /**
   * @param image  the image (or bare `{disk, path}`) to resolve; `null` when absent.
   * @param transform  a raw Cloudinary transformation string (e.g. `w_400,c_fill,ar_16:9`),
   *   written without slashes: the service adds the one between it and the path.
   *   Applied only when `disk === 'cloudinary'`; ignored for every other disk. Omit it to
   *   deliver the stored asset untransformed.
   *
   *   Never put `f_auto` or `q_auto` in it. Cloudinary bills per *derived asset* — each
   *   distinct transformation of each image — and `f_auto` multiplies that count by the
   *   number of formats it elects to serve, for every size requested, while `q_auto` adds a
   *   per-asset content analysis. Across a whole listing of insights or topics that is an
   *   open-ended bill for a difference the reader will not see. Keep to a small fixed set of
   *   sizes instead, and never compute a width from the viewport or the device pixel ratio —
   *   `w_397` and `w_400` are two separate billed assets.
   * @returns the delivery URL, or `null` when there is no image to render (callers
   *   decide their own fallback).
   */
  build(image: ImageUrlSource | Image | null | undefined, transform?: string): string | null {
    if (!image?.path) {
      return null;
    }
    switch (image.disk) {
      case 'cloudinary': {
        // No transform means no transformation component at all: Cloudinary delivers the
        // original asset and no derived asset is created (and so none is billed).
        // The service writes the one slash between the parts. A transform passed with a
        // trailing slash (`'w_400,c_fill/'`) or a path with a leading one once produced
        // `…/w_400,c_fill//path` — a different URL from the canonical one, cached apart by the
        // CDN — so stray slashes are trimmed here rather than trusted to every call site.
        const upload = `https://res.cloudinary.com/${this.imageHostIdentifier}/image/upload/`;
        const path = image.path.replace(/^\/+/, '');
        const transformation = transform?.replace(/^\/+|\/+$/g, '');

        return transformation ? `${upload}${transformation}/${path}` : `${upload}${path}`;
      }
      case 'wordpress':
      case 'external':
        return image.path; // already a full URL — never transformed
      case 'local':
        return `${this.appUrl}/storage/${image.path}`; // never transformed
      default:
        return image.path;
    }
  }
}
